import { defineStore } from "pinia";
import { ref, computed } from "vue";
import localforage from "localforage";
import { chain, get, omit } from "lodash";
import { request } from "@/axios";
import { objExt } from "@xmiles/holopix-helper";

/**
 * 生图参数接口定义
 */
export interface GenerationParams {
  prompt?: string;
  // 扩展其他可能的参数
  [key: string]: any;
}

// 参数缓存条目
interface ParamCacheEntry {
  params: GenerationParams;
  timestamp: number;
}

// 初始化 IndexedDB 实例
const generationParamStorage = localforage.createInstance({
  name: "holopix-board-data",
  storeName: "infinite-canvas-editor-generationParams"
});

/** 清空浏览器中的生图参数缓存。 */
export async function clearGenerationParamStorage(): Promise<void> {
  await generationParamStorage.clear();
}

// ============ 生图任务参数查询 ============

/**
 * 获取生图任务参数
 */
function queryGenerationParam(data: { clientId: string }) {
  return request({
    api: "/api/chatMessage/findOne",
    method: "GET",
    params: data,
    notToast: false
  });
}

/**
 * 生图参数管理 Store
 * 将生图参数从画布对象中剥离，单独维护，避免画布数据过大导致加载缓慢
 *
 * 存储策略：
 * - 内存缓存：使用 Map，访问速度最快
 * - IndexedDB：使用 localforage，持久化存储，容量更大
 */
export const useGenerationParamStore = defineStore("infinite-canvas-editor-generationParams", () => {
  // #region State
  /** 内存缓存：taskId -> 参数条目 */
  const memoryCache = ref<Map<string, ParamCacheEntry>>(new Map());

  /** 是否正在加载 */
  const isLoading = ref(false);
  // #endregion

  // #region 配置常量
  const MAX_CACHE_SIZE = 100; // 最大缓存数量（LRU策略）
  const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 缓存有效期（7天）
  // #endregion

  // #region Getters
  /** 获取缓存统计信息 */
  const stats = computed(() => ({
    memoryCount: memoryCache.value.size,
    isLoading: isLoading.value
  }));
  // #endregion

  // #region Actions
  /**
   * 获取生图参数（惰性加载）
   * 优先顺序：内存缓存 -> IndexedDB -> API请求
   */
  async function getParams(clientId: string): Promise<GenerationParams | null> {
    if (!clientId) return null;

    // 1. 先查内存缓存
    const memoryEntry = memoryCache.value.get(clientId);
    if (memoryEntry && !isExpired(memoryEntry.timestamp)) {
      return memoryEntry.params;
    }

    // 2. 再查 IndexedDB
    try {
      const localEntry = await generationParamStorage.getItem<ParamCacheEntry>(clientId);
      if (localEntry && !isExpired(localEntry.timestamp)) {
        // 同步到内存缓存
        memoryCache.value.set(clientId, localEntry);
        return localEntry.params;
      }
    } catch (error) {
      console.error("[GenerationParamStore] 读取 IndexedDB 失败:", error);
    }

    // 3. 最后请求接口
    try {
      isLoading.value = true;
      // 调用接口
      const res = await queryGenerationParam({ clientId });

      // 尝试解析返回结果
      let apiParams: GenerationParams | null = null;
      let inputStr = get(res, "data.input", "");

      apiParams = objExt.parse(inputStr);

      if (apiParams) {
        // 存入缓存
        await setParams(clientId, apiParams);
        return apiParams;
      }
    } catch (error) {
      console.error("[GenerationParamStore] 接口请求失败:", error);
    } finally {
      isLoading.value = false;
    }

    return null;
  }

  /**
   * 批量获取参数（用于导出/分享场景）
   */
  async function getParamsBatch(taskIds: string[]): Promise<Record<string, GenerationParams>> {
    isLoading.value = true;
    const result: Record<string, GenerationParams> = {};
    const missingIds: string[] = [];

    try {
      // 先查内存缓存
      for (const taskId of taskIds) {
        const memoryEntry = memoryCache.value.get(taskId);
        if (memoryEntry && !isExpired(memoryEntry.timestamp)) {
          result[taskId] = memoryEntry.params;
        } else {
          missingIds.push(taskId);
        }
      }

      // 从 IndexedDB 中查找缺失的
      if (missingIds.length > 0) {
        const stillMissing: string[] = [];

        for (const taskId of missingIds) {
          try {
            const localEntry = await generationParamStorage.getItem<ParamCacheEntry>(taskId);
            if (localEntry && !isExpired(localEntry.timestamp)) {
              result[taskId] = localEntry.params;
              memoryCache.value.set(taskId, localEntry);
            } else {
              stillMissing.push(taskId);
            }
          } catch (error) {
            stillMissing.push(taskId);
          }
        }

        // TODO: 批量请求 API 获取 stillMissing 的参数
        if (stillMissing.length > 0) {
          console.log(`[GenerationParamStore] 未缓存的参数: ${stillMissing.length} 个`);
        }
      }
    } finally {
      isLoading.value = false;
    }

    return result;
  }

  /**
   * 存储参数
   */
  async function setParams(clientId: string, params: GenerationParams): Promise<void> {
    if (!clientId || !params) return;

    const entry: ParamCacheEntry = {
      params,
      timestamp: Date.now()
    };

    // 内存缓存（LRU策略）
    if (memoryCache.value.size >= MAX_CACHE_SIZE) {
      const firstKey = memoryCache.value.keys().next().value;
      if (firstKey) {
        memoryCache.value.delete(firstKey);
      }
    }
    memoryCache.value.set(clientId, entry);

    // IndexedDB 存储
    try {
      await generationParamStorage.setItem(clientId, entry);
    } catch (error) {
      console.error("[GenerationParamStore] 写入 IndexedDB 失败:", error);
    }
  }

  /**
   * 批量存储参数
   */
  async function setParamsBatch(paramMap: Record<string, GenerationParams>): Promise<void> {
    const entries = Object.entries(paramMap);
    if (entries.length === 0) return;

    // 使用 Promise.all 并行存储
    await Promise.all(entries.map(([taskId, params]) => setParams(taskId, params)));
  }

  /**
   * 清理画布数据中的生图参数，返回清洗后的数据和参数映射
   * 这是核心优化方法，在加载画布数据前调用
   */
  function stripGenerationParams(objects: any[]): { cleanedObjects: any[]; paramMap: Record<string, GenerationParams> } {
    const paramMap: Record<string, GenerationParams> = {};

    const cleanedObjects = objects.map((obj) => {

      // 检查对象是否包含生图参数,obj.placeholderData?.generationParams;需要处理旧数据,但是这个placeholderData字段和generationParams字段已经被抛弃了
      const hasGenerationParams = obj.generationParams || obj.placeholderData?.generationParams;

      if (hasGenerationParams) {
        const taskId = obj.clientId;

        const params: GenerationParams = {
          // 保留其他可能的参数
          ...obj.generationParams,
          ...obj?.placeholderData?.generationParams
        };

        // 保存到映射表
        paramMap[taskId] = params;

        // 清理对象中的生图参数，保留其他属性
        const cleaned = chain(obj)
          .omit(["generationParams", "placeholderData"])
          .value();

        return cleaned;
      }
      return obj;
    });

    return { cleanedObjects, paramMap };
  }

  /**
   * 根据画布对象获取其生图参数
   */
  async function getParamsByObject(obj: any): Promise<GenerationParams | null> {
    const clientId = obj?.clientId;
    if (!clientId) return null;
    return getParams(clientId);
  }

  /**
   * 清理指定任务的缓存
   */
  async function clearParams(taskId: string): Promise<void> {
    memoryCache.value.delete(taskId);
    try {
      await generationParamStorage.removeItem(taskId);
    } catch (error) {
      console.error("[GenerationParamStore] 清理缓存失败:", error);
    }
  }

  /**
   * 清空所有缓存
   */
  async function clearAll(): Promise<void> {
    memoryCache.value.clear();
    try {
      await clearGenerationParamStorage();
    } catch (error) {
      console.error("[GenerationParamStore] 清空缓存失败:", error);
    }
  }

  /**
   * 清理过期缓存
   */
  async function cleanupExpired(): Promise<void> {
    try {
      const keys = await generationParamStorage.keys();
      let cleanedCount = 0;

      for (const key of keys) {
        const entry = await generationParamStorage.getItem<ParamCacheEntry>(key);
        if (entry && isExpired(entry.timestamp)) {
          await generationParamStorage.removeItem(key);
          memoryCache.value.delete(key);
          cleanedCount++;
        }
      }

      if (cleanedCount > 0) {
        console.log(`[GenerationParamStore] 清理 ${cleanedCount} 个过期缓存`);
      }
    } catch (error) {
      console.error("[GenerationParamStore] 清理过期缓存失败:", error);
    }
  }
  // #endregion

  // #region 私有方法
  function isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > CACHE_TTL;
  }
  // #endregion

  return {
    // State
    memoryCache,
    isLoading,

    // Getters
    stats,

    // Actions
    getParams,
    getParamsBatch,
    setParams,
    setParamsBatch,
    stripGenerationParams,
    getParamsByObject,
    clearParams,
    clearAll,
    cleanupExpired
  };
});
